import { TabLinks } from "components/TabLinks";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { Outlet, useLocation } from "react-router-dom";

// Single source of truth for the staff CERS tabs, in workflow order (review
// decisions -> work rejections -> fix matching). The tab bar below is built from
// this list and main.tsx builds the nested child routes from the same list, so a
// tab can never exist without a route (or a route without a tab).
export const CERS_BASE_PATH = "/staff/cers";

export const CERS_TABS = [
  { segment: "submittals", title: "Submittals" },
  { segment: "rejections", title: "Rejections" },
  { segment: "facilities", title: "Facilities" },
  { segment: "cupa-conflicts", title: "CUPA Conflicts" },
  { segment: "analytics", title: "Analytics" },
  { segment: "pre-post", title: "Pre/Post Encamp" },
  { segment: "coverage", title: "Coverage" },
] as const;

export type CersTabSegment = (typeof CERS_TABS)[number]["segment"];

export const cersTabPath = (segment: CersTabSegment) => `${CERS_BASE_PATH}/${segment}`;

const TAB_LINKS = CERS_TABS.map((tab) => ({ title: tab.title, to: cersTabPath(tab.segment) }));

export function CersTabs() {
  const { pathname } = useLocation();

  // "CERS" is the parent crumb; each child page adds its own ("Submittals" etc.)
  // so the trail reads "CERS / Submittals". The provider orders crumbs by `to`,
  // so the base path sorts ahead of the tab paths.
  useBreadcrumb({ label: "CERS", to: CERS_BASE_PATH });

  // Only show the tab bar on one of the real tab routes, so it stays hidden
  // during the bare-/staff/cers -> submittals redirect (and on any future
  // non-tab child) with no special-casing.
  const activeTab = TAB_LINKS.find((tab) => pathname.startsWith(tab.to));

  return (
    <>
      {activeTab && <TabLinks tabs={TAB_LINKS} value={activeTab.to} aria-label="CERS tabs nav" />}
      <Outlet />
    </>
  );
}
